import React from 'react';
import SEO from '../components/SEO';
import './LegalPage.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <SEO
        title="Privacy Policy – Remotix: Universal TV Remote"
        description="Read the Privacy Policy for Remotix: Universal TV Remote. Learn how we collect, use, and protect your information."
      />
      <section className="page-hero">
        <div className="page-hero-bg">
          <div className="hero-glow hero-glow-1"></div>
        </div>
        <div className="page-hero-content">
          <span className="section-badge">Legal</span>
          <h1>Privacy <span className="gradient-text">Policy</span></h1>
          <p>Your privacy is important to us. Learn how we handle your data.</p>
        </div>
      </section>

      <section className="legal-content">
        <div className="legal-container">
          <p className="legal-updated">Last updated: March 26, 2026</p>

          <div className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Remotix: Universal TV Remote ("we", "our", or "us") is committed to protecting your privacy. This Privacy
              Policy explains how we collect, use, disclose, and safeguard your information when you use our
              mobile application.
            </p>
          </div>

          <div className="legal-section">
            <h2>2. Information We Collect</h2>
            <h3>2.1 Information Collected Automatically</h3>
            <ul>
              <li><strong>Device Information:</strong> Device model, operating system version, unique device identifiers, and mobile network information.</li>
              <li><strong>Usage Data:</strong> App feature usage, interaction patterns, crash reports, and performance data collected through Firebase Analytics and Crashlytics.</li>
              <li><strong>Network Information:</strong> WiFi network details necessary for device discovery and TV connection functionality.</li>
              <li><strong>IP Address:</strong> Collected for device discovery and network communication purposes.</li>
            </ul>

            <h3>2.2 Information You Provide</h3>
            <ul>
              <li><strong>Device Preferences:</strong> Selected TV brands, paired devices, and remote control configurations stored locally on your device.</li>
              <li><strong>Language Preferences:</strong> Your selected language for the app interface.</li>
              <li><strong>Custom Playlists:</strong> IPTV playlist files you choose to import, stored locally on your device.</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information to:</p>
            <ul>
              <li>Provide and maintain the App's core remote control functionality.</li>
              <li>Discover and connect to compatible devices on your network.</li>
              <li>Improve app performance and user experience through analytics.</li>
              <li>Diagnose and fix technical issues through crash reporting.</li>
              <li>Deliver relevant advertisements through our advertising partners.</li>
              <li>Send push notifications (with your consent) for updates and features.</li>
              <li>Customize and remember your preferences and settings.</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>4. Third-Party Services</h2>
            <p>Our App uses the following third-party services that may collect information:</p>
            <ul>
              <li><strong>Google Firebase:</strong> Analytics, Crashlytics, Remote Config, and Cloud Messaging for app analytics, crash reporting, feature configuration, and push notifications.</li>
              <li><strong>Google AdMob:</strong> For displaying advertisements. AdMob may collect device information and usage data to serve personalized ads.</li>
              <li><strong>Google Play Services:</strong> For app distribution and in-app review functionality.</li>
            </ul>
            <p>
              Each third-party service has its own privacy policy governing the use of your information.
              We encourage you to review their policies.
            </p>
          </div>

          <div className="legal-section">
            <h2>5. Data Storage</h2>
            <p>
              Most of your personal data (paired devices, preferences, remote configurations) is stored
              locally on your device using SharedPreferences and is not transmitted to our servers. Analytics
              and crash data are processed by Firebase services according to Google's privacy practices.
            </p>
          </div>

          <div className="legal-section">
            <h2>6. Permissions</h2>
            <p>The App may request the following permissions:</p>
            <ul>
              <li><strong>WiFi/Network Access:</strong> Required to discover and connect to smart TVs on your local network.</li>
              <li><strong>IR Blaster:</strong> Required for infrared remote control functionality.</li>
              <li><strong>Storage:</strong> Required to import and manage IPTV playlist files.</li>
              <li><strong>Internet:</strong> Required for IPTV streaming, ads, analytics, and screen casting features.</li>
              <li><strong>Notifications:</strong> Optional, for receiving app updates and feature announcements.</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>7. Advertising</h2>
            <p>
              We display advertisements through Google AdMob. Ads may be personalized based on your interests,
              which are determined by your activity across apps and websites. You can opt out of personalized
              advertising in your device settings under Google Ads settings.
            </p>
          </div>

          <div className="legal-section">
            <h2>8. Children's Privacy</h2>
            <p>
              Our App is not intended for children under 13 years of age. We do not knowingly collect personal
              information from children under 13. If you are a parent or guardian and believe your child has
              provided us with personal information, please contact us so we can take appropriate action.
            </p>
          </div>

          <div className="legal-section">
            <h2>9. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your information.
              However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee
              absolute security.
            </p>
          </div>

          <div className="legal-section">
            <h2>10. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you.</li>
              <li>Request deletion of your personal data.</li>
              <li>Opt out of personalized advertising.</li>
              <li>Withdraw consent for push notifications.</li>
              <li>Request a copy of your data in a portable format.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </div>

          <div className="legal-section">
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting
              the new Privacy Policy within the App and updating the "Last updated" date. Your continued use of
              the App after changes constitutes acceptance of the updated policy.
            </p>
          </div>

          <div className="legal-section">
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices, please
              contact us at:
            </p>
            <p className="contact-email">
              <a href="mailto:support@universaltvremote.app">support@universaltvremote.app</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
